// source: template-project@7007a338a apps/backoffice/src/components/ui/selection.ts
/**
 * The four selection levels of Trafiflow UI v3 (DESIGN_SYSTEM.md §2.4–2.7) as
 * class strings, so a menu row, a tab or an option says "selected" the same
 * way everywhere. Utilities only: the same classes the gallery's Selection
 * hierarchy section draws by hand.
 *
 *   rail       Level 1 — a menu entry (rail, main sidebar, project menu): grey under ink.
 *   menu       Level 1 — the same grey under ink, for a menu row inside a page.
 *   title-tab  Level 2 — the pill: a submenu tab or a chosen option, light blue under ink.
 *              (The title tabs beside the h1 are an underline: Tabs `variant="title"`.)
 *   card-tab   Level 3 — inside a card: 10% ink.
 *
 * Every level is `rounded-md` (10px; Law 2.7) and hovers on `ink-a05`.
 * Replaces the old `SELECTED_SURFACE_CLASS`: `menu` is the same
 * light blue under ink, written as utilities instead of `var(--dst-…)`.
 */

export type SelectionLevel = 'rail' | 'title-tab' | 'menu' | 'card-tab';

/** One line per level, from DESIGN_SYSTEM.md §2. */
export const SELECTION_LEVELS: Readonly<Record<SelectionLevel, string>> = {
  rail: 'Level 1, where you are: every menu entry (the rail, the main sidebar, the project menu) selects grey under ink (bg-ds-menu-active-bg text-ds-menu-active-text), icon in ink. Never the accent, never the tabs’ blue (Law 2.4).',
  menu: 'Level 1, a menu row inside a page (the project menu): the same grey under ink as the sidebar (Law 2.4).',
  'title-tab': 'Level 2, where you are in the page: the submenu row under the title band and a dropdown’s chosen option sit on the light blue under ink (bg-ds-tab-active-bg text-ds-tab-active-text). The title tabs beside the h1 are no box: accent text over a 2px accent line (Tabs variant="title", Law 2.5).',
  'card-tab': 'Level 3, inside a card: tabs, pills and segmented controls select with 10% ink (bg-ds-ink-a10), ink text (Law 2.6).',
};

const SELECTED: Readonly<Record<SelectionLevel, string>> = {
  rail: 'bg-ds-menu-active-bg text-ds-menu-active-text',
  'title-tab': 'bg-ds-tab-active-bg text-ds-tab-active-text',
  menu: 'bg-ds-menu-active-bg text-ds-menu-active-text',
  'card-tab': 'bg-ds-ink-a10 text-foreground',
};

const UNSELECTED: Readonly<Record<SelectionLevel, string>> = {
  rail: 'text-foreground',
  'title-tab': 'text-ds-text-2',
  menu: 'text-ds-text-2',
  'card-tab': 'text-ds-text-2',
};

/** Hover on any unselected item (Law 2.7). */
export const SELECTION_HOVER_CLASS = 'hover:bg-ds-ink-a05 hover:text-foreground';

export interface SelectionSurfaceOptions {
  level: SelectionLevel;
  selected: boolean;
  /** Add the `ink-a05` hover on an unselected item. Default true; false for static rows (a list, a legend). */
  hover?: boolean;
}

/**
 * The classes for one item at one level: `rounded-md` plus the level's selected
 * or unselected surface, plus the hover when unselected.
 *
 * @example
 * <li className={cn('flex h-8 items-center px-2 text-body-sm', selectionSurface({ level: 'menu', selected }))}>
 */
export function selectionSurface({ level, selected, hover = true }: SelectionSurfaceOptions): string {
  if (selected) return `rounded-md ${SELECTED[level]}`;
  return hover ? `rounded-md ${UNSELECTED[level]} ${SELECTION_HOVER_CLASS}` : `rounded-md ${UNSELECTED[level]}`;
}
